import dayjs from 'dayjs'
import { useCallback, useState } from 'react'

import {
  QuickInterval,
  TariffType
} from '@/api/models/parking/lib/parking.types'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import {
  syncServerTime,
  useServerTimeStore
} from '@/components/entities/common/model/server-time'
import { DateRange } from '@/components/shared/datepicker/ui/Calendar.types'
import { getFirstBookableHour } from '@/components/shared/datepicker/utils/getFirstBookableHour'
import { getRangeFromQuickInterval } from '@/components/shared/datepicker/utils/getRangeFromQuickInterval'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'

import { getDaySlotsRange, getMonthSlotsRange, toApiDate } from '../lib/dates'

type Props = {
  selectedTab: TariffType
  period: DateRange
  quickIntervals: Record<TariffType, QuickInterval[]>
}

export const useRentCalendar = ({
  selectedTab,
  period,
  quickIntervals
}: Props) => {
  const { reachGoal } = useYandexMetrika()
  const serverOffsetMs = useServerTimeStore(state => state.serverOffsetMs)

  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [isServerTimeLoading, setIsServerTimeLoading] = useState(false)
  const [slotsPeriod, setSlotsPeriod] = useState(() => ({
    from: toApiDate(new Date()),
    to: toApiDate(new Date())
  }))

  const getDefaultPeriod = useCallback(
    (type: TariffType) => {
      const defaultInterval = quickIntervals[type].find(
        filter => filter.isDefault
      )
      const serverNow = Date.now() + serverOffsetMs

      if (!defaultInterval) {
        const now = dayjs(serverNow).toDate()
        return { from: now, to: now }
      }

      return getRangeFromQuickInterval(defaultInterval)
    },
    [quickIntervals, serverOffsetMs]
  )

  const getCalendarIntervals = useCallback(
    () => quickIntervals[selectedTab],
    [quickIntervals, selectedTab]
  )

  const handleCalendarOpenChange = useCallback(
    (open: boolean) => {
      setIsCalendarOpen(open)

      if (!open) {
        return
      }

      const serverTimePromise = syncServerTime()

      if (serverTimePromise !== null) {
        setIsServerTimeLoading(true)
        void serverTimePromise
          .catch(() => undefined)
          .finally(() => setIsServerTimeLoading(false))
      }

      const serverNow = Date.now() + serverOffsetMs
      const fallbackDate = new Date(serverNow)

      const periodFrom = period.from ?? fallbackDate
      const firstBookableDate = getFirstBookableHour(serverNow).toDate()

      const visibleDate =
        selectedTab === TariffType.Hourly && periodFrom < firstBookableDate
          ? firstBookableDate
          : periodFrom

      setSlotsPeriod(
        selectedTab === TariffType.Daily
          ? getMonthSlotsRange(visibleDate)
          : getDaySlotsRange(visibleDate)
      )

      if (selectedTab === TariffType.Daily) {
        reachGoal(YandexMetrikaEvent.PASSES_RENT_OPEN_CALENDAR_DAYS)
      } else {
        reachGoal(YandexMetrikaEvent.PASSES_RENT_OPEN_CALENDAR_HOURS)
      }
    },
    [selectedTab, period.from, serverOffsetMs, reachGoal]
  )

  const handleCalendarMonthChange = useCallback(
    (month: Date) => {
      if (!isCalendarOpen) {
        return
      }

      setSlotsPeriod(
        selectedTab === TariffType.Hourly
          ? getDaySlotsRange(month)
          : getMonthSlotsRange(month)
      )
    },
    [selectedTab, isCalendarOpen]
  )

  const onQuickFilterClick = useCallback(() => {
    if (selectedTab === TariffType.Daily) {
      reachGoal(YandexMetrikaEvent.PASSES_RENT_QUICK_FILTERS_DAYS)
    } else {
      reachGoal(YandexMetrikaEvent.PASSES_RENT_QUICK_FILTERS_HOURS)
    }
  }, [selectedTab, reachGoal])

  return {
    isCalendarOpen,
    isServerTimeLoading,
    slotsPeriod,
    setSlotsPeriod,
    getDefaultPeriod,
    getCalendarIntervals,
    handleCalendarOpenChange,
    handleCalendarMonthChange,
    onQuickFilterClick
  }
}
